import { Navigate, Route, Routes } from 'react-router-dom'
import { useAuth } from './hooks/useAuth.js'
import { AppShell } from './components/AppShell.js'
import { SuperAdminLayout } from './components/SuperAdminLayout.js'
import { LoginPage } from './pages/Login.js'
import { JoinPage } from './pages/Join.js'
import { ConnectWordPressPage } from './pages/ConnectWordPress.js'
import { DashboardPage } from './pages/Dashboard.js'
import { OrdersPage } from './pages/Orders.js'
import { StoresPage } from './pages/Stores.js'
import { CustomersPage } from './pages/Customers.js'
import { AnalyticsPage } from './pages/Analytics.js'
import { BuyNowButtonPage, CheckoutDesignPage, CheckoutSettingsPage, CodRulesPage } from './pages/Checkout.js'
import { GatewaysPage, RefundsPage, TransactionsPage } from './pages/Payments.js'
import { AbandonedCheckoutsPage, NotificationsPage } from './pages/Engagement.js'
import { AddMoneyPage, BillingPage, PlansPage, WalletOverviewPage, WalletTransactionsPage } from './pages/Wallet.js'
import {
  ApiWebhooksPage,
  CustomWebsiteIntegrationPage,
  ShopifyIntegrationPage,
  WordPressIntegrationPage,
} from './pages/Integrations.js'
import { BundlesPage, CountdownPage } from './pages/Marketing.js'
import { DiscountsPage } from './pages/Discounts.js'
import { ShippingPage } from './pages/Shipping.js'
import { SettingsPage } from './pages/Settings.js'
import {
  AdminAnalyticsPage,
  AdminHealthPage,
  AdminIntegrationsPage,
  AdminLedgerPage,
  AdminMerchantsPage,
  AdminOrdersPage,
  AdminPaymentsPage,
  AdminSettingsPage,
  AdminStoresPage,
  AdminUserPage,
  AdminUsersPage,
  AdminWhatsAppPage,
} from './pages/admin/Admin.js'
import { SuperAdminDashboard } from './pages/admin/SuperAdminDashboard.js'
import { AdminProfilePage } from './pages/admin/AdminProfile.js'
import { AdminWalletAlertsPage } from './pages/admin/wallet-alerts/Dashboard.js'
import { AdminWalletAlertWizardPage } from './pages/admin/wallet-alerts/Wizard.js'
import { AdminWalletAlertRulePage } from './pages/admin/wallet-alerts/Detail.js'
import { AdminWalletAlertHistoryPage } from './pages/admin/wallet-alerts/History.js'
import { AdminWalletAlertTemplatesPage } from './pages/admin/wallet-alerts/Templates.js'
import { Toaster } from './components/feedback.js'

export function App(): React.ReactNode {
  const { user, shop, loading, embedded, embeddedError } = useAuth()

  // The distinction that matters: `loading` is "we do not know yet", and
  // rendering the sign-in screen during it would flash a login form at a
  // merchant who is already signed in, on every page load.
  if (loading) {
    return (
      <div className="boot">
        <div className="spinner" aria-hidden="true" />
        <p>Loading your dashboard…</p>
      </div>
    )
  }

  const signedIn = Boolean(user ?? shop)

  /*
   * Inside the Shopify admin there is nothing to sign in to.
   *
   * A Shopify merchant has no account with us and no password — Shopify
   * vouching for them is the whole credential — so falling through to the
   * routes below would put a sign-in form in the admin's iframe with no way
   * past it. This says what actually went wrong instead, which is nearly always
   * one of two things an operator can fix.
   */
  if (embedded && !signedIn) {
    return (
      <div className="boot">
        <h1>Could not open Outfalak Checkout</h1>
        <p>{embeddedError ?? 'This store is not connected yet.'}</p>
        <p className="muted">
          Try reloading this page. If it keeps happening, reinstall Outfalak Checkout from your Shopify admin.
        </p>
      </div>
    )
  }

  if (!signedIn) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/*
          Rendered rather than redirected to, so the URL survives sign-in. A
          merchant arriving from their own WordPress admin carries the whole
          connect request in the query string, and a redirect to /login would
          drop it — sending them back to WordPress to start again, having done
          nothing wrong.
        */}
        <Route path="/connect/*" element={<LoginPage />} />
        {/*
          Same reason as the connect route above: an invite link carries its
          token in the path, and bouncing to /login would drop it — leaving
          somebody at a sign-in screen with no idea why and a link they can no
          longer produce.
        */}
        <Route path="/join/:token" element={<JoinPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  return (
    <Routes>
      {/*
        Two layouts, two trees, and each role gets exactly one of them. The
        admin branch is guarded here *and* by the API's principal check — hiding
        a route is a UI convenience, not a permission, and an operator route
        that trusts the client is a hole. Anything else an operator lands on,
        including `/login` straight after signing in and the `/` that Google
        sign-in returns to, goes to the console.
      */}
      <Route path="/connect/wordpress" element={<ConnectWordPressPage />} />
      <Route path="/join/:token" element={<JoinPage />} />
      <Route path="/admin/*" element={user?.isAdmin ? <SuperAdminRoutes /> : <Navigate to="/" replace />} />
      <Route path="*" element={user?.isAdmin ? <Navigate to="/admin" replace /> : <MerchantRoutes />} />
    </Routes>
  )
}

function MerchantRoutes(): React.ReactNode {
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<DashboardPage />} />

        <Route path="/stores" element={<StoresPage />} />
        {/* Adding a store happens on the integration pages; old links land there. */}
        <Route path="/stores/add" element={<Navigate to="/integrations" replace />} />
        <Route path="/stores/:platform" element={<StoresPage />} />

        <Route path="/orders" element={<OrdersPage />} />
        {/* Abandoned orders live under Engagement; the Orders sub-item points
            at the same screen rather than duplicating it. */}
        <Route path="/orders/abandoned" element={<AbandonedCheckoutsPage />} />
        <Route path="/orders/analytics" element={<AnalyticsPage />} />

        <Route path="/customers" element={<CustomersPage />} />

        <Route path="/checkout" element={<Navigate to="/checkout/buy-now" replace />} />
        <Route path="/checkout/design" element={<CheckoutDesignPage />} />
        <Route path="/checkout/buy-now" element={<BuyNowButtonPage />} />
        <Route path="/checkout/cod" element={<CodRulesPage />} />
        <Route path="/checkout/settings" element={<CheckoutSettingsPage />} />
        <Route path="/checkout/discounts" element={<DiscountsPage />} />
        <Route path="/checkout/shipping" element={<ShippingPage />} />

        <Route path="/payments" element={<Navigate to="/payments/transactions" replace />} />
        <Route path="/payments/transactions" element={<TransactionsPage />} />
        <Route path="/payments/gateways" element={<GatewaysPage />} />
        <Route path="/payments/refunds" element={<RefundsPage />} />

        <Route path="/shopify-product" element={<Navigate to="/shopify-product/countdown" replace />} />
        <Route path="/shopify-product/countdown" element={<CountdownPage />} />
        <Route path="/shopify-product/bundles" element={<BundlesPage />} />

        {/* Old /marketing links stay alive. */}
        <Route path="/marketing" element={<Navigate to="/shopify-product/countdown" replace />} />
        <Route path="/marketing/countdown" element={<Navigate to="/shopify-product/countdown" replace />} />
        <Route path="/marketing/bundles" element={<Navigate to="/shopify-product/bundles" replace />} />

        <Route path="/engagement" element={<Navigate to="/engagement/abandoned" replace />} />
        <Route path="/engagement/abandoned" element={<AbandonedCheckoutsPage />} />
        <Route path="/engagement/whatsapp" element={<Navigate to="/engagement/notifications" replace />} />
        <Route path="/engagement/notifications" element={<NotificationsPage />} />

        <Route path="/wallet" element={<WalletOverviewPage />} />
        <Route path="/wallet/transactions" element={<WalletTransactionsPage />} />
        <Route path="/wallet/add-money" element={<AddMoneyPage />} />
        <Route path="/wallet/plans" element={<PlansPage />} />
        <Route path="/wallet/billing" element={<BillingPage />} />

        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/analytics/:view" element={<AnalyticsPage />} />

        <Route path="/integrations" element={<Navigate to="/integrations/shopify" replace />} />
        <Route path="/integrations/shopify" element={<ShopifyIntegrationPage />} />
        <Route path="/integrations/wordpress" element={<WordPressIntegrationPage />} />
        <Route path="/integrations/custom" element={<CustomWebsiteIntegrationPage />} />
        <Route path="/integrations/api" element={<ApiWebhooksPage />} />

        <Route path="/settings" element={<SettingsPage />} />
        {/* Plans are retired — every account is unlimited. Old links land on Settings. */}
        <Route path="/settings/plan" element={<Navigate to="/settings" replace />} />
        <Route path="/settings/:section" element={<SettingsPage />} />

        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  )
}

function SuperAdminRoutes(): React.ReactNode {
  return (
    <SuperAdminLayout>
      {/*
        Relative paths, not absolute. This <Routes> is a descendant of the
        `/admin/*` route, so an absolute `/merchants` here is a router error
        rather than a match.
      */}
      <Routes>
        <Route index element={<SuperAdminDashboard />} />
        <Route path="merchants" element={<AdminMerchantsPage />} />
        <Route path="stores" element={<AdminStoresPage />} />
        <Route path="orders" element={<AdminOrdersPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="users/:id" element={<AdminUserPage />} />

        <Route path="ledger" element={<AdminLedgerPage />} />
        <Route path="recharges" element={<AdminLedgerPage />} />
        <Route path="commission" element={<AdminLedgerPage />} />

        <Route path="wallet-alerts" element={<AdminWalletAlertsPage />} />
        <Route path="wallet-alerts/new" element={<AdminWalletAlertWizardPage />} />
        <Route path="wallet-alerts/history" element={<AdminWalletAlertHistoryPage />} />
        <Route path="wallet-alerts/templates" element={<AdminWalletAlertTemplatesPage />} />
        <Route path="wallet-alerts/:id" element={<AdminWalletAlertRulePage />} />
        <Route path="wallet-alerts/:id/edit" element={<AdminWalletAlertWizardPage />} />

        <Route path="analytics" element={<AdminAnalyticsPage />} />
        <Route path="whatsapp" element={<AdminWhatsAppPage />} />
        <Route path="health" element={<AdminHealthPage />} />
        <Route path="payments" element={<AdminPaymentsPage />} />
        <Route path="integrations" element={<AdminIntegrationsPage />} />
        <Route path="settings" element={<AdminSettingsPage />} />
        <Route path="profile" element={<AdminProfilePage />} />

        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
      <Toaster />
    </SuperAdminLayout>
  )
}
